import React, {
	FC, useEffect, useMemo, useRef, useState,
} from 'react';
import {
	Counter,
	ColumnPicker,
	SaveForLater,
	useOpen,
	AngleLeft,
	SaveToList,
	Floaty,
	Button,
	TrashCanIcon,
	getObjectsDiff,
	alephToast,
	NotificationType,
	NotificationToast,
} from '@aleph/shared-components';
import useCreateSpotifyReport from 'wb-spotify/app/routes/reports/create/useCreateSpotifyReport';
import { motion } from 'framer-motion';
import { useApolloClient } from '@apollo/client';
import SpotifyDatasourceSelector from 'wb-spotify/app/routes/reports/create/spotifyDatasourceSelector';
import SpotifyReportScheduler from 'wb-spotify/app/routes/reports/create/scheduler';
import useCreateReportMutation from 'wb-spotify/app/routes/reports/create/useCreateReport';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import ToggleLifetimeStats from 'wb-spotify/app/routes/superGrid/tool/toggleLifetimestats';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import {
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED,
	SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED,
} from 'wb-spotify/app/routes/superGrid/statistics/utils';
import {
	SPOTIFY_REPORT_BREAKDOWN_OPTIONS_CATEGORIZED,
	SPOTIFY_REPORT_BREAKDOWN_OPTIONS_LIFETIME,
	SPOTIFY_REPORT_COL_DEFS,
	SPOTIFY_REPORTS_DEFAULT_COL_DEF,
} from 'wb-spotify/app/routes/utils';
import { getSpotifyReportsDatasource } from 'wb-spotify/app/routes/reports/create/reportsDatasource';
import SpotifyReportsBreakdownSelector from 'wb-spotify/app/routes/reports/create/breakdownSelector';
import { useNavigate } from 'react-router-dom';

import SpotifyReportLoadingCellRenderer from 'wb-spotify/app/routes/reports/create/cellRenderers/loading';
import SpotifyReportsNoRowsOverlay from 'wb-spotify/app/routes/reports/create/cellRenderers/noRows';
import { NameInput } from 'wb-spotify/app/routes/reports/create/nameInput';

import useSaveBeforeExitDialog from 'wb-reddit/app/routes/hooks/useSaveBeforeExitDialog';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';

const reportRowClass = ['bg-white', 'border-0'];

const CreateSpotifyReportContainer: FC = () => {
	const { t } = useTranslation();
	const { lifetimeStats } = useSupergridSettings();
	const navigate = useNavigate();

	const {
		reportId, filters, breakdowns, metrics, setMetrics, dates, cancelCreation, name, setName, setBreakdowns,
	} = useCreateSpotifyReport();

	const isEdit = useMemo(() => !!reportId, [reportId]);
	// TODO refactor this - on creation always switch metrics and breakdown when toggle lifetime stats
	// on edit do not apply this logic when first opened
	const [initEdit, setInitEdit] = useState(false);
	const [duplicateMetrics] = useState(metrics);
	const [duplicateBreakdowns] = useState(breakdowns);

	useEffect(() => {
		if (!isEdit || initEdit) {
			if (lifetimeStats) {
				setMetrics(SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED[0].metrics.map((item => item.value)));
				setBreakdowns([SPOTIFY_REPORT_BREAKDOWN_OPTIONS_LIFETIME[0].value]);
			} else {
				setMetrics(SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED[0].metrics.map((item => item.value)));
				setBreakdowns([SPOTIFY_REPORT_BREAKDOWN_OPTIONS_CATEGORIZED[0].value]);
			}
		}

		if (!initEdit) {
			setInitEdit(true);
		}
	}, [lifetimeStats, setMetrics, setBreakdowns]);

	useEffect(() => {
		// TODO make more work solution for duplicate reports
		// or rework lifeteime stats changing
		if (name.includes('Duplicate of ')) {
			setBreakdowns(duplicateBreakdowns);
			setMetrics(duplicateMetrics);
		}
	}, []);

	const initialReportData = useRef({
		filters,
		breakdowns,
		metrics,
		dates,
		name,
	});

	function cancelReportCreation() {
		navigate('/reports');
		cancelCreation();
	}

	const {
		beforeClose,
		dialog: saveBeforeExitModal,
		setShouldPromptOnClose: setShouldShowSaveBeforeExit,
		shouldPromptOnClose: hasChanges,
	} = useSaveBeforeExitDialog({
		onConfirm: cancelReportCreation,
	});

	useEffect(
		() => {
			// In creation mode, always show the save before exit
			if (!isEdit) {
				setShouldShowSaveBeforeExit(true);

				return;
			}

			const diff = getObjectsDiff(initialReportData.current, {
				filters,
				breakdowns,
				metrics,
				dates,
				name,
			}, true);

			setShouldShowSaveBeforeExit(
				!!Object.keys(diff).length,
			);
		},
		[filters, breakdowns, metrics, dates, name, isEdit],
	);

	const client = useApolloClient();
	const platformUser = useUserMetaData();
	const [api, setApi] = useState<GridApi | null>(null);
	const [
		datasourceSelectorOpen,
		{ onOpen: onOpenDatasourceSelector, onClose: onCloseDatasourceSelector },
	] = useOpen(false);

	const { onCreateReport, loading, error } = useCreateReportMutation();

	const onGridReady = (e: GridReadyEvent) => {
		setApi(e.api);
		e.api.sizeColumnsToFit();
	};

	useEffect(() => {
		if (error) {
			console.error(error);
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t(`sp: report ${isEdit ? 'edit' : 'creation'} error`, `Error during report ${isEdit ? 'edit' : 'creation'}`)}
				/>,
			);
		}
	}, [error]);

	const reportServerSideDatasource = useMemo(() => getSpotifyReportsDatasource({
		client,
		filters,
		metrics,
		breakdowns,
		dates,
	}), [client, dates, metrics, filters, breakdowns]);

	useEffect(() => {
		if (api) {
			api.refreshServerSide({ purge: true });
		}
	}, [reportServerSideDatasource]);

	const onCreateReportClick = async () => {
		await onCreateReport();
		cancelReportCreation();
	};

	const columnDefs = useMemo(() => SPOTIFY_REPORT_COL_DEFS.filter(
		colDef => metrics.includes(colDef.field ?? '')
			|| breakdowns.includes(colDef.field ?? '')
			|| colDef.field === 'index',
	), [metrics, breakdowns]);

	useEffect(() => {
		api?.sizeColumnsToFit();
	}, [metrics, breakdowns]);

	const showFloaty = useMemo(() => !isEdit || hasChanges, [isEdit, hasChanges]);

	return (
		<motion.div
			className="flex flex-col w-full flex-1 pl-[20px]"
			initial={{ filter: 'blur(10px)' }}
			animate={{ filter: 'blur(0px)' }}
			exit={{ filter: 'blur(10px)' }}
			transition={{ duration: 0.1 }}
		>
			<div className="flex w-full h-[64px] gap-[32px] justify-between border-b border-b-black">
				<div className='shrink w-full h-full p-0 text-black overflow-hidden'>
					<div className='flex w-full h-full items-center gap-[8px]'>
						<Button
							className='ml-[1px]'
							onClick={beforeClose}
							leftNode={<AngleLeft />}
							appearance="ghost"
							color="gray"
							data-testid="back-button"
						/>

						<NameInput
							name={name}
							setName={setName}
						/>
					</div>
				</div>

				<SpotifyReportScheduler />
			</div>

			<div className="py-[16px] flex justify-between">
				<div className="flex gap-[8px]">
					{datasourceSelectorOpen
						&& <SpotifyDatasourceSelector onClose={onCloseDatasourceSelector} />
					}
					<div className="relative">
						<Button
							title={t('re: reports.data_source', 'Data source')}
							leftNode={<SaveForLater />}
							appearance="ghost"
							onClick={onOpenDatasourceSelector}
							data-testid="datasource-button"
						/>
						<Counter
							count={filters?.userGroupId?.[0]?.value?.length ?? 0}
							className="top-[2px] left-[17px]"
							variant="square-accent"
							data-testid="datasource-counter"
						/>
					</div>
					<SpotifyReportsBreakdownSelector />
					<ToggleLifetimeStats />
				</div>

				<ColumnPicker
					metricCategories={lifetimeStats
						? SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED
						: SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED}
					selectedMetrics={metrics}
					onConfirm={setMetrics}
					data-testid="reports-metrics-button"
				/>
			</div>

			<div className="ag-theme-alpine reports-grid flex-1"
				data-testid="reports-grid"
			>
				<AgGridReact
					rowModelType="serverSide"
					serverSideDatasource={reportServerSideDatasource}
					defaultColDef={SPOTIFY_REPORTS_DEFAULT_COL_DEF}
					columnDefs={columnDefs}
					loadingCellRenderer={SpotifyReportLoadingCellRenderer}
					onGridReady={onGridReady}
					maxConcurrentDatasourceRequests={1}
					suppressContextMenu
					suppressRowTransform
					suppressMovableColumns
					suppressCellFocus
					noRowsOverlayComponent={SpotifyReportsNoRowsOverlay}
					rowClass={reportRowClass}
					headerHeight={32}
					rowHeight={32}
					ensureDomOrder={true}
					enableCellTextSelection={true}
				/>
			</div>

			{
				showFloaty && (
					<Floaty>
						{
							isEdit
								? (
									hasChanges && (
										<Button
											title={t('sc: discard changes', 'Discard changes')}
											appearance="outlined"
											color="red"
											onClick={beforeClose}
											data-testid="discard-report-button"
										/>
									)
								)
								: (
									<Button
										leftNode={
											<TrashCanIcon />
										}
										appearance="outlined"
										color="red"
										onClick={beforeClose}
									/>
								)
						}

						{
							!platformUser?.readOnly
					&& (
						<Button
							title={t('re: reports.save', 'Save report')}
							leftNode={<SaveToList />}
							color="green"
							loading={loading}
							onClick={onCreateReportClick}
							data-testid="save-report-button"
						/>
					)
						}
					</Floaty>
				)
			}

			{saveBeforeExitModal}
		</motion.div>
	);
};

export default CreateSpotifyReportContainer;
